import CheckIcon from "@mui/icons-material/Check";
import ErrorIcon from "@mui/icons-material/Error";
import Alert from "@mui/joy/Alert";
import Box from "@mui/joy/Box";
import Divider from "@mui/joy/Divider";
import FormControl from "@mui/joy/FormControl";
import FormHelperText from "@mui/joy/FormHelperText";
import FormLabel from "@mui/joy/FormLabel";
import Input from "@mui/joy/Input";
import Link from "@mui/joy/Link";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import { keyframes } from "@mui/system";
import React from "react";
import { MFAType } from "../../../models/auth";
import theme from "../../constants/theme";
import proSanteConnectLogo from "../../assets/ProSanteConnect_sidentifier_BLEU.svg";
import resilienceLogo from "../../assets/resilience-logo.svg";
import starWhite from "../../assets/star_white.svg";
import LoadingButton from "./LoadingButton";
import MFAChallenge from "./MFAChallenge";
import PasswordResetForm from "./PasswordResetForm";

export interface EmailCheckNotification {
  error: boolean;
  message: string;
}

interface AuthPageProps {
  readonly loading: boolean;
  readonly onSubmit: (email: string, password: string) => void;
  readonly errors: { email?: string; password?: string };
  readonly cloud?: boolean;
  readonly hidePSCButton?: boolean;
  readonly pscConfig?: any;
  readonly mfaType?: MFAType;
  readonly email_check_notification?: EmailCheckNotification | null;
}

export default function AuthPage({
  loading,
  onSubmit,
  errors,
  cloud = false,
  hidePSCButton = false,
  pscConfig,
  mfaType,
  email_check_notification = null,
}: AuthPageProps) {
  const [tab, setTab] = React.useState(0);
  const [secondPage, setSecondPage] = React.useState<MFAType | "passwordReset" | null>(
    mfaType || null
  );
  const [notification, setNotification] = React.useState<EmailCheckNotification | null>(
    email_check_notification || null
  );
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");

  const shakeAnimation = keyframes`
      0% { transform: translateX(0); }
      10%, 30%, 50%, 70%, 90% { transform: translateX(-10px); }
      20%, 40%, 60%, 80% { transform: translateX(10px); }
      100% { transform: translateX(0); }
  `;

  const initResetPassword = () => {
    setTab(1);
    setSecondPage("passwordReset");
  };

  React.useEffect(() => {
    if (mfaType) {
      setTab(1);
      setSecondPage(mfaType);
    }
  }, [mfaType]);

  React.useEffect(() => {
    if (tab !== 0) {
      setNotification(null);
    } else {
      setNotification(email_check_notification);
    }
  }, [tab, email_check_notification, notification]);

  const renderSecondPageContent = () => {
    if (secondPage === "passwordReset") {
      return <PasswordResetForm goBack={() => setTab(0)} />;
    }

    if (secondPage === MFAType.SMS_MFA) {
      return (
        <MFAChallenge description="We just sent an SMS on your phone. Please enter the code to continue." />
      );
    }

    if (secondPage === MFAType.SOFTWARE_TOKEN_MFA) {
      return (
        <MFAChallenge description="Please enter the code from your authenticator app to continue." />
      );
    }

    return null;
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100vw",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        alignItems: { xs: "flex-start", sm: "center" },
        justifyContent: "center",
        backgroundColor: theme.palette.primary[500], // #5e6f6e
        backgroundImage: `
          radial-gradient(at 10% 43%, #bec7d8 0px, transparent 50%),
          radial-gradient(at 41% 25%, #e0d2aa 0px, transparent 50%),
          radial-gradient(at 0% 0%, #5e6f6e 0px, transparent 50%),
          radial-gradient(at 80% 0%, #8f9ab5 0px, transparent 50%),
          radial-gradient(at 0% 50%, #882d2d 0px, transparent 50%),
          radial-gradient(at 73% 58%, #c8b790 0px, transparent 50%),
          radial-gradient(at 0% 100%, #bfad87 0px, transparent 50%),
          radial-gradient(at 80% 100%, #7d877f 0px, transparent 50%);
            `,
      }}
    >
      {notification && (
        <Alert
          startDecorator={notification.error ? <ErrorIcon /> : <CheckIcon />}
          sx={{ width: { xs: "100%", sm: "400px" } }}
          color={notification.error ? "danger" : "success"}
        >
          {notification.message}
        </Alert>
      )}
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          position: "absolute",
          height: "100%",
          width: "100%",
          aspectRatio: "1/1",
          backgroundPosition: "center",
          backgroundImage: `url(${starWhite})`,
          backgroundSize: "contain",
          filter: "blur(17px)",
          backgroundRepeat: "no-repeat",
        }}
      />
      <Box
        sx={{
          zIndex: 1,
          width: { xs: "100%", sm: "400px" },
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          background: "white",
          p: { xs: 2, sm: 4 },
          borderRadius: { xs: 0, sm: "8px" },
          transition: "all .33s ease-out",
          height: { xs: "100vh", sm: "initial" },
          maxHeight: { xs: "100vh", sm: tab === 0 ? "700px" : "500px" },
          animation: Object.keys(errors).length > 0 ? `${shakeAnimation} 0.5s linear` : "none",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <img src={resilienceLogo} alt="Resilience Logo" style={{ width: "200px" }} />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            overflow: "hidden",
            width: "100%",
          }}
        >
          <Box
            sx={{
              //transform: `translateX(-${String(tab * 100)}%)`,
              opacity: tab !== 0 ? "0" : "1",
              transition: "all 0.33s ease",
              flexShrink: "0",
              width: "100%",
            }}
          >
            <form
              onSubmit={(e: React.FormEvent) => {
                e.preventDefault();
                if (tab === 0 && !loading) {
                  onSubmit(email, password);
                }
              }}
            >
              <Box sx={{ mt: 2 }}>
                <FormControl disabled={loading} error={!!errors.email}>
                  <FormLabel>Email</FormLabel>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <FormHelperText>{errors.email}</FormHelperText>
                </FormControl>
                <FormControl disabled={loading} sx={{ mt: 2 }} error={!!errors.password}>
                  <FormLabel>Password</FormLabel>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <FormHelperText>{errors.password}</FormHelperText>
                </FormControl>
                <Stack gap={2}>
                  {cloud && (
                    <Box
                      sx={{
                        textAlign: "right",
                      }}
                    >
                      <Link level="body-sm" onClick={() => initResetPassword()}>
                        Forgot your password?
                      </Link>
                    </Box>
                  )}
                  <LoadingButton
                    loading={loading}
                    sx={{ mt: 2 }}
                    type="submit"
                    fullWidth
                    color="neutral"
                  >
                    Sign in
                  </LoadingButton>
                </Stack>
              </Box>
            </form>
            <Box>
              {cloud && !hidePSCButton && (
                <>
                  <Divider sx={{ my: 4 }} />
                  <Box
                    sx={{
                      textAlign: "center",
                      transition: "all .33s ease-out",
                      ":hover": { transform: "scale(1.05)" },
                    }}
                  >
                    <a
                      href={
                        pscConfig.url +
                        "/auth?response_type=code&scope=openid scope_all&client_id=" +
                        pscConfig.client_id +
                        "&redirect_uri=" +
                        pscConfig.redirect_uri +
                        "&acr_values=eidas1"
                      }
                    >
                      <img
                        src={proSanteConnectLogo}
                        alt="logo pro sante connect"
                        style={{ width: "260px" }}
                      />
                    </a>
                  </Box>
                </>
              )}
            </Box>
          </Box>
          <Box
            sx={{
              transform: `translateX(-${tab * 100}%)`,
              transition: "transform 0.33s ease",
              flexShrink: "0",
              width: "100%",
            }}
          >
            {renderSecondPageContent()}
          </Box>
        </Box>
        <Box component="footer" sx={{ mt: 4 }}>
          <Typography level="body-xs" textAlign="center">
            ©{" "}
            <Link component="a" href="https://www.resilience.care/" target="_blank" rel="noopener">
              Resilience
            </Link>{" "}
            {new Date().getFullYear()}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
